import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { LeagueCustomization, CustomizationType, DesignData } from '@/types/customization';
import { generateSimpleCSS } from '@/utils/simpleColorUtils';
import type { SimpleDesignData } from '@/components/SimpleDesignEditor';
export const useLeagueCustomization = (
  leagueId: string, 
  customizationType: CustomizationType = 'standard',
  designTag?: string
) => {
  const [customization, setCustomization] = useState<LeagueCustomization | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const fetchCustomization = async () => {
    try {
      setLoading(true);
      
      // Build query
      let query = supabase
        .from('league_customizations')
        .select('*')
        .eq('league_id', leagueId)
        .eq('customization_type', customizationType)
        .eq('is_active', true);

      // If design_tag is specified, filter by it
      if (designTag) {
        query = query.eq('design_tag', designTag);
      }

      const { data: initialData, error } = await query.maybeSingle();
      let data = initialData;

      // If no customization found with specified type, try to get any active customization
      // Prioritize: enterprise > premium > standard
      if (!data) {
        const { data: anyCustomization } = await supabase
          .from('league_customizations')
          .select('*')
          .eq('league_id', leagueId)
          .eq('is_active', true)
          .order('customization_type', { ascending: false }) // enterprise > premium > standard
          .limit(1)
          .maybeSingle();
        
        data = anyCustomization;
      }

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching customization:', error);
        return;
      }

      setCustomization(data as LeagueCustomization);
    } catch (error) {
      console.error('Error fetching customization:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveCustomization = async (
    designData: DesignData, 
    customCss?: string,
    type: CustomizationType = customizationType,
    tag?: string
  ) => {
    console.log('[useLeagueCustomization] Saving customization:', { designData, customCss, type, tag, existingId: customization?.id });
    try {
      setSaving(true);

      // Get organization_id for this league
      const { data: leagueData } = await supabase
        .from('leagues')
        .select('organization_id')
        .eq('id', leagueId)
        .single();

      if (!leagueData) throw new Error('League not found');

      if (customization && !tag) {
        // Update existing customization (without tag means updating current one)
        const { error } = await supabase
          .from('league_customizations')
          .update({
            design_data: designData as any,
            custom_css: customCss,
            customization_type: type,
            is_active: true,
            updated_at: new Date().toISOString(),
          })
          .eq('id', customization.id);

        if (error) throw error;
      } else {
        // Check if a customization with this tag already exists
        let query = supabase
          .from('league_customizations')
          .select('id')
          .eq('league_id', leagueId)
          .eq('customization_type', type);

        if (tag) {
          query = query.eq('design_tag', tag);
        }

        const { data: existing } = await query.maybeSingle();

        if (existing) {
          // Update existing tagged customization
          const { error } = await supabase
            .from('league_customizations')
            .update({
              design_data: designData as any,
              custom_css: customCss,
              is_active: true,
              updated_at: new Date().toISOString(),
            })
            .eq('id', existing.id);

          if (error) throw error;
        } else {
          // Create new customization with tag
          const { data, error } = await supabase
            .from('league_customizations')
            .insert({
              league_id: leagueId,
              organization_id: leagueData.organization_id,
              customization_type: type,
              design_data: designData as any,
              custom_css: customCss,
              design_tag: tag || null,
              is_active: true,
            })
            .select()
            .single();

          if (error) throw error;
          setCustomization(data as LeagueCustomization);
        }
      }

      // Update local state directly instead of refetching (prevents race condition)
      if (customization) {
        setCustomization({
          ...customization,
          design_data: designData as any,
          custom_css: customCss,
          customization_type: type,
          updated_at: new Date().toISOString(),
        });
      }

      toast({
        title: 'Customization saved',
        description: 'Your league design has been updated successfully.',
      });
    } catch (error) {
      console.error('Error saving customization:', error);
      toast({
        title: 'Error',
        description: 'Failed to save customization. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const deleteCustomization = async () => {
    if (!customization) return;

    try {
      setSaving(true);
      const { error } = await supabase
        .from('league_customizations')
        .update({ is_active: false })
        .eq('id', customization.id);

      if (error) throw error;

      setCustomization(null);
      toast({
        title: 'Customization removed',
        description: 'League design has been reset to default.',
      });
    } catch (error) {
      console.error('Error deleting customization:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove customization. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  /**
   * Simple, reliable CSS generation.
   * Uses the new SimpleDesignData format with just theme, primaryColor, secondaryColor.
   * Also supports legacy DesignData for backward compatibility.
   */
  const generateCSS = useCallback((designData: DesignData | SimpleDesignData, customCss?: string): string => {
    // Extract simple values - works with both old and new format
    const theme = (designData.theme as 'light' | 'dark') || 'dark';
    const primaryColor = designData.primaryColor || '#3B82F6';
    const secondaryColor = designData.secondaryColor || '#374151';
    const backgroundColor = (designData as SimpleDesignData).backgroundColor;

    // Use the simple CSS generator
    return generateSimpleCSS(theme, primaryColor, secondaryColor, customCss, backgroundColor);
  }, []);

  useEffect(() => {
    if (leagueId) {
      fetchCustomization();
    }
  }, [leagueId, customizationType, designTag]);

  return {
    customization,
    loading,
    saving,
    saveCustomization,
    deleteCustomization,
    refreshCustomization: fetchCustomization,
    generateCSS,
  };
};